import React from "react";
import HTMLBubble from "./HTMLBubble";
import ImagesGrid from "./ImagesGrid";
import CardsList from "./CardsList";
import ItemsList from "./ItemsList";
import SuggestionChips from "./SuggestionChips";

/**
 * StructuredRenderer Component
 * Unified renderer for block-based LLM responses
 * Renders blocks in the order they appear in the blocks array
 */
const StructuredRenderer = ({
  structuredResponse,
  onButtonClick,
  onSuggestionClick,
  disabled = false,
  excludeSuggestions = false,
  sessionData = null,
  metadata = null,
  trackingSessionId = null,
}) => {
  console.log("=== STRUCTURED RENDERER DEBUG ===");
  console.log("structuredResponse:", JSON.stringify(structuredResponse, null, 2));
  console.log("structuredResponse type:", typeof structuredResponse);

  // Defensive: Check if structuredResponse exists
  if (!structuredResponse) {
    console.error("=== RENDERER ERROR: No structuredResponse provided ===");
    return <div className="structured-response structured-error">No response data</div>;
  }

  // Defensive: Check if blocks exists and is an array
  if (!structuredResponse.blocks) {
    console.error("=== RENDERER ERROR: structuredResponse.blocks is missing ===");
    console.error("structuredResponse keys:", Object.keys(structuredResponse));
    return <div className="structured-response structured-error">Missing blocks data</div>;
  }

  if (!Array.isArray(structuredResponse.blocks)) {
    console.error("=== RENDERER ERROR: structuredResponse.blocks is not an array ===");
    console.error("Type of blocks:", typeof structuredResponse.blocks);
    console.error("blocks value:", structuredResponse.blocks);
    return <div className="structured-response structured-error">Invalid blocks format</div>;
  }

  // Defensive: Check if blocks array is empty
  if (structuredResponse.blocks.length === 0) {
    console.warn("=== RENDERER WARNING: blocks array is empty ===");
    return <div className="structured-response structured-error">No content to display</div>;
  }

  console.log("=== RENDERER: Rendering", structuredResponse.blocks.length, "blocks ===");

  return (
    <div className="structured-response">
      {structuredResponse.blocks.map((block, index) => {
        console.log("=== RENDERER: Processing block", index, "type:", block?.type);

        // Defensive: Check if block exists
        if (!block) {
          console.error("=== RENDERER ERROR: Block at index", index, "is null/undefined ===");
          return null;
        }

        // Defensive: Check if block has type
        if (!block.type) {
          console.error("=== RENDERER ERROR: Block at index", index, "has no type ===");
          console.error("Block data:", block);
          return null;
        }

        try {
          switch (block.type) {
            case "rich_text":
              // Render rich text block (either text or html)
              if (block.html) {
                return <HTMLBubble key={index} html={block.html} />;
              } else if (block.text) {
                return (
                  <div key={index} className="html-bubble structured-html">
                    <p>{block.text}</p>
                  </div>
                );
              }
              return null;

            case "card_list":
              // Render card list block
              if (!block.cards || !Array.isArray(block.cards)) {
                console.error("=== RENDERER ERROR: Invalid cards in card_list block ===");
                return null;
              }
              return (
                <CardsList 
                  key={index} 
                  cards={block.cards} 
                  onButtonClick={onButtonClick} 
                  sessionData={sessionData}
                  metadata={metadata}
                  trackingSessionId={trackingSessionId}
                />
              );

            case "image_gallery":
              // Render image gallery block
              if (!block.images || !Array.isArray(block.images)) {
                console.error("=== RENDERER ERROR: Invalid images in image_gallery block ===");
                return null;
              }
              return <ImagesGrid key={index} images={block.images} />;

            case "list":
              // Render list block
              if (!block.items || !Array.isArray(block.items)) {
                console.error("=== RENDERER ERROR: Invalid items in list block ===");
                return null;
              }
              return <ItemsList key={index} items={block.items} />;

            case "suggestions":
              // Render suggestions block (should be at the end)
              // Skip if excludeSuggestions is true (they'll be rendered outside the message bubble)
              if (excludeSuggestions) {
                return null;
              }
              if (!block.chips || !Array.isArray(block.chips)) {
                console.error("=== RENDERER ERROR: Invalid chips in suggestions block ===");
                return null;
              }
              return (
                <SuggestionChips
                  key={index}
                  chips={block.chips}
                  onSuggestionClick={onSuggestionClick}
                  disabled={disabled}
                />
              );

            default:
              console.warn("=== RENDERER WARNING: Unknown block type:", block.type, "===");
              return null;
          }
        } catch (error) {
          console.error("=== RENDERER ERROR: Exception while rendering block", index, "===");
          console.error("Block:", block);
          console.error("Error:", error);
          return (
            <div key={index} className="structured-response structured-error">
              Error rendering block {index + 1}
            </div>
          );
        }
      })}
    </div>
  );
};

export default StructuredRenderer;
